/**
 * A API, tipada e num sítio só.
 *
 * O projecto Bamidi-main tinha um objecto `api` equivalente, e era a melhor
 * ideia que lá estava: cada ecrã chamava `api.produtos()` em vez de montar
 * caminhos à mão. Isto é o mesmo, com os tipos do domínio a acompanhar, para
 * que uma resposta que mude de forma parta na compilação e não no ecrã.
 */

import { api } from './client';
import { getMesaSessionToken, getMesaToken } from './mesa';
import type {
  Analytics,
  Allergen,
  MenuCategory,
  ModifierGroup,
  ModifierGroupWrite,
  ModifierOption,
  ModifierOptionWrite,
  Order,
  OrderState,
  Product,
  ProductWrite,
  PushConfig,
  OrderHoursSettings,
  ResumoDiario,
  ServiceRequest,
  StaffAccount,
  TableInfo,
  TableSessionInfo,
  SalesFilters,
  TableRow,
  TenantRole,
} from '../types/domain';

const dados = <T>(resposta: { data: T }): T => resposta.data;

export interface CreateOrderPayload {
  nome_cliente: string;
  metodo_pagamento: 'app' | 'balcony';
  observacao?: string;
  itens: { produto: number; quantidade: number; extras: number[]; sabores_bolas_ids?: number[] }[];
}

/** Leituras do cliente. O token da mesa é sempre exigido pelo servidor. */
export const clienteApi = {
  resolverMesa: (token: string) =>
    api.post<TableSessionInfo>('/api/mesas/sessao/', { mesa_token: token }).then(dados),

  validarSessaoMesa: () => api.get<{ expira_em: string }>('/api/mesas/sessao/', {
    params: { mesa_token: getMesaToken() }, headers: { 'X-Table-Session': getMesaSessionToken() }, timeout: 10_000,
  }).then(dados),

  atividadeMesa: () => api.patch<{ expira_em: string }>('/api/mesas/sessao/', {
    mesa_token: getMesaToken(),
  }, { headers: { 'X-Table-Session': getMesaSessionToken() }, timeout: 10_000 }).then(dados),

  menu: () =>
    api.get<Product[]>('/api/produtos/', { params: { menu_cliente: 1 } }).then(dados),

  /** Os que mais saíram nos últimos dias. Vazio se ainda não houver vendas. */
  destaques: () => api.get<Product[]>('/api/produtos/destaques/').then(dados),

  recomendacoes: () => api.get<Product[]>('/api/produtos/recomendacoes/').then(dados),

  categorias: () => api.get<MenuCategory[]>('/api/categorias/').then(dados),

  chamarFuncionario: (tableToken: string) => api.post<ServiceRequest>('/api/solicitacoes/', {
    mesa_token: tableToken,
  }, { timeout: 10_000 }).then(dados),

  chamadaPendente: (tableToken: string) => api.get<ServiceRequest | null>('/api/solicitacoes/pendente/', {
    params: { mesa_token: tableToken }, timeout: 10_000,
  }).then(dados),

  meusPedidos: () =>
    api
      .get<Order[]>('/api/pedidos/', { params: { mesa_token: getMesaToken() } })
      .then(dados),

  pedido: (id: number) =>
    api
      .get<Order>(`/api/pedidos/${id}/`, { params: { mesa_token: getMesaToken() } })
      .then(dados),

  criarPedido: (payload: CreateOrderPayload, submissionKey: string, tableToken = getMesaToken()) =>
    api
      .post<Order>('/api/pedidos/', { ...payload, mesa_token: tableToken }, {
        headers: { 'Idempotency-Key': submissionKey },
        timeout: 20_000,
      })
      .then(dados),

};

/** Operação: atendimento e chamadas de mesa. */
export const operacaoApi = {
  acaoOffline: (action: import('../lib/staffOffline').StaffAction) => api.post(`/api/pedidos/${action.orderId}/offline-action/`, {
    action_id: action.id, kind: action.kind, expected: action.expected,
  }, { timeout: 15_000 }).then(dados),
  pedidosMobile: () => api.get<Order[]>('/api/pedidos/mobile/', { timeout: 15_000 }).then(dados),
  historicoAtendimento: (token: string) => api.get<Order[]>('/api/pedidos/historico-atendimento/', { headers: { 'X-Attendance-History': token }, timeout: 15_000 }).then(dados),
  desbloquearHistorico: (password: string) => api.post<{ token: string }>('/api/attendance/history-access/', { password }, { timeout: 15_000 }).then(dados),
  atividadeHistorico: (token: string) => api.patch('/api/attendance/history-access/', undefined, { headers: { 'X-Attendance-History': token }, timeout: 10_000 }).then(dados),
  bloquearHistorico: (token: string) => api.delete('/api/attendance/history-access/', { headers: { 'X-Attendance-History': token }, timeout: 5000 }).then(dados),

  aprovar: (id: number) => api.post(`/api/pedidos/${id}/approve/`, undefined, { timeout: 15_000 }).then(dados),
  recusar: (id: number) => api.post(`/api/pedidos/${id}/reject/`, undefined, { timeout: 15_000 }).then(dados),
  cancelar: (id: number) => api.post(`/api/pedidos/${id}/cancelar/`, undefined, { timeout: 15_000 }).then(dados),
  marcarVisto: (id: number) => api.post(`/api/pedidos/${id}/visto/`).then(dados),
  assumir: (id: number) => api.post<Order>(`/api/pedidos/${id}/assumir/`, undefined, { timeout: 15_000 }).then(dados),
  assumirAprovar: (id: number) => api.post<Order>(`/api/pedidos/${id}/assumir-aprovar/`, undefined, { timeout: 15_000 }).then(dados),
  libertar: (id: number) => api.post<Order>(`/api/pedidos/${id}/libertar/`, undefined, { timeout: 15_000 }).then(dados),
  marcarRegistado: (id: number) =>
    api.post(`/api/pedidos/${id}/registado/`, undefined, { timeout: 15_000 }).then(dados),

  reabrirRegisto: (id: number, registadoEm: string, token: string) =>
    api.post<Order>(`/api/pedidos/${id}/reabrir-registo/`, { registado_em: registadoEm }, { headers: { 'X-Attendance-History': token }, timeout: 15_000 }).then(dados),

  aprovarTodos: () =>
    api.post<{ updated: number }>('/api/pedidos/approve-all/').then(dados),
  solicitacoesPendentes: () =>
    api
      .get<ServiceRequest[]>('/api/solicitacoes/', { params: { status: 'P' }, timeout: 15_000 })
      .then(dados),

  resolverSolicitacao: (id: number) =>
    api.patch(`/api/solicitacoes/${id}/`, { estado: 'R' }, { timeout: 15_000 }).then(dados),
};

export const pushApi = {
  configuracao: () => api.get<PushConfig>('/api/push/config/', { timeout: 10_000 }).then(dados),
  testar: (endpoint: string) => api.post<{ sent: number }>('/api/push/test/', { endpoint }, { timeout: 15_000 }).then(dados),
  registar: (subscription: PushSubscriptionJSON, deviceName: string) =>
    api
      .post('/api/push/subscription/', {
        ...subscription,
        device_name: deviceName,
      }, { timeout: 10_000 })
      .then(dados),
  remover: (endpoint: string) =>
    api.delete('/api/push/subscription/', { data: { endpoint }, timeout: 5000 }).then(dados),
};

/** Gestão: histórico, menu, mesas, contas. */
export const gestaoApi = {
  analytics: (filters?: SalesFilters) => api.get<Analytics>('/api/manager/analytics/', { params: filters }).then(dados),

  historico: (filtros: {
    data?: string;
    mesa?: string;
    estado?: string;
    por_registar?: boolean;
  }) =>
    api
      .get<Order[]>('/api/pedidos/historico/', {
        params: {
          data: filtros.data || undefined,
          mesa: filtros.mesa || undefined,
          estado: filtros.estado || undefined,
          por_registar: filtros.por_registar ? 1 : undefined,
        },
      })
      .then(dados),

  resumo: (data?: string) =>
    api
      .get<ResumoDiario>('/api/pedidos/resumo/', { params: { data: data || undefined } })
      .then(dados),

  exportarCsv: (filters?: SalesFilters) =>
    api.get('/api/manager/export/', { params: filters, responseType: 'blob' }).then(dados),

  // --- menu ---
  produtos: (incluirArquivados = false) => api.get<Product[]>('/api/produtos/', incluirArquivados ? { params: { incluir_arquivados: 1 } } : undefined).then(dados),
  criarProduto: (payload: ProductWrite) =>
    api.post<Product>('/api/produtos/', payload).then(dados),
  atualizarProduto: (id: number, payload: ProductWrite) =>
    api.patch<Product>(`/api/produtos/${id}/`, payload).then(dados),
  horariosPedidos: () =>
    api.get<OrderHoursSettings>('/api/produtos/horarios/').then(dados),
  definirHorariosPedidos: (enabled: boolean) =>
    api.patch<OrderHoursSettings>('/api/produtos/horarios/', { enabled }).then(dados),
  guardarHorarios: (payload: OrderHoursSettings) =>
    api.patch<OrderHoursSettings>('/api/produtos/horarios/', payload).then(dados),
  alergenios: () => api.get<Allergen[]>('/api/alergenios/').then(dados),
  atualizarImagemProduto: (id: number, imagem: File) => {
    const form = new FormData();
    form.append('imagem', imagem);
    return api.patch<Product>(`/api/produtos/${id}/`, form).then(dados);
  },
  removerImagemProduto: (id: number) =>
    api.patch<Product>(`/api/produtos/${id}/`, { imagem: null }).then(dados),
  eliminarProduto: (id: number) => api.delete(`/api/produtos/${id}/`).then(dados),
  arquivarProduto: (id: number) => api.post<Product>(`/api/produtos/${id}/arquivar/`).then(dados),
  restaurarProduto: (id: number) => api.post<Product>(`/api/produtos/${id}/restaurar/`).then(dados),
  ordenarRecomendacoes: (ids: number[]) =>
    api.post('/api/produtos/ordenar-recomendacoes/', { ids }).then(dados),

  categorias: () => api.get<MenuCategory[]>('/api/categorias/').then(dados),
  criarCategoria: (payload: Partial<MenuCategory>) =>
    api.post<MenuCategory>('/api/categorias/', payload).then(dados),
  atualizarCategoria: (id: number, payload: Partial<MenuCategory>) =>
    api.patch<MenuCategory>(`/api/categorias/${id}/`, payload).then(dados),
  eliminarCategoria: (id: number) => api.delete(`/api/categorias/${id}/`).then(dados),

  opcoes: () => api.get<ModifierOption[]>('/api/opcoes/').then(dados),
  criarOpcao: (payload: ModifierOptionWrite) =>
    api.post<ModifierOption>('/api/opcoes/', payload).then(dados),
  atualizarOpcao: (id: number, payload: ModifierOptionWrite) =>
    api.patch<ModifierOption>(`/api/opcoes/${id}/`, payload).then(dados),
  eliminarOpcao: (id: number) => api.delete(`/api/opcoes/${id}/`).then(dados),

  grupos: () => api.get<ModifierGroup[]>('/api/grupos-modificadores/').then(dados),
  criarGrupo: (payload: ModifierGroupWrite) =>
    api.post<ModifierGroup>('/api/grupos-modificadores/', payload).then(dados),
  atualizarGrupo: (id: number, payload: ModifierGroupWrite) =>
    api.patch<ModifierGroup>(`/api/grupos-modificadores/${id}/`, payload).then(dados),
  eliminarGrupo: (id: number) => api.delete(`/api/grupos-modificadores/${id}/`).then(dados),

  // --- mesas ---
  mesas: () => api.get<TableRow[]>('/api/mesas/').then(dados),
  criarMesa: (numero: number) =>
    api.post<TableRow>('/api/mesas/', { numero }).then(dados),
  atualizarMesa: (id: number, payload: Partial<TableRow>) =>
    api.patch<TableRow>(`/api/mesas/${id}/`, payload).then(dados),
  eliminarMesa: (id: number): Promise<void> => api.delete(`/api/mesas/${id}/`).then(() => undefined),

  // --- contas ---
  staff: () => api.get<StaffAccount[]>('/api/staff/').then(dados),
  criarConta: (payload: { username: string; password: string; role: TenantRole }) =>
    api.post<StaffAccount>('/api/staff/', payload).then(dados),
  atualizarConta: (id: number, payload: { role?: TenantRole; ativo?: boolean }) =>
    api.patch<StaffAccount>(`/api/staff/${id}/`, payload).then(dados),
  mudarPassword: (id: number, password: string) =>
    api.post(`/api/staff/${id}/password/`, { password }).then(dados),
  removerAcesso: (id: number) => api.delete(`/api/staff/${id}/`).then(dados),
};
